import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useLogo } from '../context/LogoContext';
import { api } from '../utils/api';
import { isWebAuthnSupported, startAuthentication } from '../utils/webauthn';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Fingerprint, ShieldCheck } from 'lucide-react';
import LanguageSelector from '../components/LanguageSelector';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [twoFACode, setTwoFACode] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const { loginWithToken } = useAuth();
  const { t } = useLanguage();
  const { logoUrl } = useLogo();
  const navigate = useNavigate();
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    const supported = isWebAuthnSupported();
    setBiometricAvailable(supported);

    if (supported) {
      autoTriggerBiometric();
    } else {
      setShowPasswordForm(true);
    }

    return () => { mountedRef.current = false; };
  }, []);

  const completeLogin = (data) => {
    loginWithToken(data.access_token, data.user);
    toast.success(t('login_success'));
    navigate(data.user.role === 'admin' ? '/admin/dashboard' : '/dashboard');
  };

  // Auto-trigger biometric on page load
  const autoTriggerBiometric = async () => {
    setBiometricLoading(true);
    try {
      const optionsRes = await axios.post(`${API_URL}/api/webauthn/login/options`);
      const credential = await startAuthentication(optionsRes.data);
      const verifyRes = await api.verifyWebAuthnLogin(credential);
      if (mountedRef.current) completeLogin(verifyRes.data);
    } catch {
      if (mountedRef.current) setShowPasswordForm(true);
    } finally {
      if (mountedRef.current) setBiometricLoading(false);
    }
  };

  // Manual biometric login button
  const handleBiometricLogin = async () => {
    setBiometricLoading(true);
    try {
      const optionsRes = await axios.post(`${API_URL}/api/webauthn/login/options`);
      const credential = await startAuthentication(optionsRes.data);
      const verifyRes = await api.verifyWebAuthnLogin(credential);
      completeLogin(verifyRes.data);
    } catch (error) {
      if (error.name === 'NotAllowedError') {
        toast.error(t('profile_bio_cancel'));
      } else {
        toast.error(t('login_bio_error'));
      }
    } finally {
      setBiometricLoading(false);
    }
  };

  // Password login
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      const data = response.data;

      if (data.requires_2fa) {
        setRequires2FA(true);
        setTempToken(data.temp_token);
        setTwoFACode('');
        return;
      }

      completeLogin(data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(t('login_email_not_verified'), {
          action: { label: t('verify_button'), onClick: () => window.location.href = '/verify-email' },
        });
      } else {
        toast.error(error.response?.data?.detail || t('login_fail'));
      }
    } finally {
      setLoading(false);
    }
  };

  // 2FA verify
  const handle2FAVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/2fa/verify-login?token=${tempToken}&code=${twoFACode}`);
      completeLogin(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('profile_2fa_invalid_code'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary smooth-transition" data-testid="back-link">
            <ArrowLeft className="h-4 w-4" />
            {t('back_to_home')}
          </Link>
          <LanguageSelector />
        </div>
        <div className="bg-card border border-border/50 rounded-2xl shadow-lg p-8">
          <div className="text-center mb-8">
            <div className="font-logo text-3xl font-bold text-primary mb-2 flex items-center justify-center gap-3">
              <img src={logoUrl} alt="PRC Remit" className="h-12 w-12 rounded-full object-cover" />
              PRC Remit
            </div>
            <p className="text-sm text-muted-foreground">{t('login_desc')}</p>
          </div>

          {/* 2FA Step */}
          {requires2FA ? (
            <form onSubmit={handle2FAVerify} className="space-y-6" data-testid="2fa-form">
              <div className="flex items-center justify-center gap-2 mb-2">
                <ShieldCheck className="h-5 w-5 text-accent" />
                <span className="text-sm font-medium text-primary">{t('login_2fa_title')}</span>
              </div>
              <p className="text-sm text-center text-muted-foreground">{t('login_2fa_desc')}</p>
              <Input
                type="text"
                placeholder="000000"
                value={twoFACode}
                onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                className="text-center text-2xl font-mono tracking-[0.5em] h-14"
                autoFocus
                data-testid="2fa-login-code"
              />
              <Button type="submit" className="w-full rounded-full h-12" disabled={loading || twoFACode.length < 6} data-testid="2fa-login-submit">
                {loading ? t('login_2fa_verifying') : t('login_2fa_verify')}
              </Button>
              <button type="button" onClick={() => { setRequires2FA(false); setTwoFACode(''); }} className="w-full text-sm text-muted-foreground hover:text-primary text-center">
                {t('back')}
              </button>
            </form>
          ) : (
            <>
              {/* Auto biometric loading */}
              {biometricLoading && !showPasswordForm && (
                <div className="flex flex-col items-center gap-4 py-8" data-testid="biometric-auto-loading">
                  <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center animate-pulse">
                    <Fingerprint className="h-12 w-12 text-primary" />
                  </div>
                  <p className="text-sm text-muted-foreground">{t('login_bio_waiting')}</p>
                </div>
              )}

              {/* Password form + biometric button */}
              {showPasswordForm && (
                <>
                  {biometricAvailable && (
                    <div className="mb-6">
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full h-14 rounded-full gap-3 text-base border-2 border-primary/30 hover:border-primary hover:bg-primary/5"
                        onClick={handleBiometricLogin}
                        disabled={biometricLoading}
                        data-testid="biometric-login-button"
                      >
                        <Fingerprint className="h-6 w-6 text-primary" />
                        {biometricLoading ? '...' : t('login_biometric')}
                      </Button>
                      <div className="flex items-center gap-4 my-6">
                        <div className="flex-1 h-px bg-border"></div>
                        <span className="text-xs text-muted-foreground">{t('login_or')}</span>
                        <div className="flex-1 h-px bg-border"></div>
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
                    <div className="space-y-2">
                      <Label htmlFor="email">{t('email')}</Label>
                      <Input id="email" type="email" placeholder="example@email.com" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="email-input" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password">{t('password')}</Label>
                      <Input id="password" type="password" placeholder="********" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="password-input" />
                    </div>
                    <Button type="submit" className="w-full rounded-full h-12" disabled={loading} data-testid="login-submit-button">
                      {loading ? '...' : t('login')}
                    </Button>
                  </form>
                </>
              )}
            </>
          )}

          <div className="mt-6 text-center text-sm">
            <span className="text-muted-foreground">{t('login_no_account')} </span>
            <Link to="/register" className="text-primary font-medium hover:underline" data-testid="register-link">
              {t('register')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
